/**
 * Master Navigation Menu Configuration
 *
 * All available menu items are defined here.
 * Visibility is controlled by tenant.theme.enabledMenus from the database
 * (every leaf slug is a key in api-core's shared/config/menu-registry.ts).
 *
 * Organised by what the user is doing, in plain words, at most two levels deep:
 *   Dashboards · Customers · Pro Forma · Orders · Sales · Purchases ·
 *   e-Invoicing (send to / receive from LHDN, Peppol, InvoiceNow) · Reports ·
 *   Settings · Help. Jurisdiction-specific items are pruned by menuFilter.js.
 *
 * Structure:
 * - slug: Unique identifier used for filtering (must match DB enabledMenus values)
 * - url: Route path
 * - name: Display name
 * - icon: Feather icon name
 * - i18n: Optional translation key
 * - info: Plain-language explanation shown in the sidebar (i) tooltip; translatable
 *   via navInfo.<slug> in i18nData.js, otherwise shown as written here
 * - submenu: Optional array of child menu items
 */

export default [
  {
    slug: 'dashboard',
    url: '/dashboard',
    name: 'Dashboard',
    icon: 'HomeIcon',
    i18n: 'nav.dashboard',
    info: 'Your business at a glance: sales totals, amounts owed to you and recent activity.',
  },
  {
    slug: 'e-invoice-dashboard',
    url: '/EInvoiceDashboard',
    name: 'E-Invoice Dashboard',
    icon: 'FileTextIcon',
    i18n: 'nav.eInvoiceDashboard',
    info: 'How your e-invoices are doing with LHDN: sent, accepted and rejected.',
  },
  {
    slug: 'peppol-dashboard',
    url: '/PeppolDashboard',
    name: 'Peppol Dashboard',
    icon: 'GridIcon',
    i18n: 'nav.peppolDashboard',
    info: 'How your InvoiceNow documents are doing: sent, received and reported to IRAS.',
  },
  {
    slug: 'my-peppol-dashboard',
    url: '/my-peppol-dashboard',
    name: 'MY Peppol Dashboard',
    icon: 'GridIcon',
    i18n: 'nav.myPeppolDashboard',
    info: 'How your documents are doing on the Peppol network: sent and received.',
  },
  {
    slug: 'customers',
    url: '/customers',
    name: 'Customers',
    icon: 'UserIcon',
    i18n: 'nav.customers',
    submenu: [
      {
        slug: 'customers-list',
        url: '/customers',
        name: 'Customers',
        i18n: 'nav.customers',
        info: 'People and companies you bill, with their tax and contact details.',
      },
      {
        slug: 'customer-groups',
        url: '/customer-groups',
        name: 'Customer Groups',
        i18n: 'nav.customerGroups',
        info: 'Group customers so you can manage them together.',
      },
    ],
  },
  {
    slug: 'pro-formas',
    url: '/pro-forma-invoices',
    name: 'Pro Forma Invoices',
    icon: 'ListIcon',
    i18n: 'nav.proFormaInvoices',
    info: 'Preliminary invoices issued before the final invoice. Kept separate from Sales and Purchases; not sent to the tax authority.',
  },
  {
    slug: 'orders',
    url: '/orders',
    name: 'Orders',
    icon: 'ShoppingBagIcon',
    i18n: 'nav.orders',
    info: 'Orders you create and send over InvoiceNow (Peppol). Kept separate from Sales and Purchases, as Peppol does not classify an order as either.',
  },
  {
    slug: 'sales',
    url: '/invoices',
    name: 'Sales',
    icon: 'TrendingUpIcon',
    i18n: 'nav.sales',
    submenu: [
      {
        slug: 'invoices',
        url: '/invoices',
        name: 'Invoices',
        i18n: 'nav.invoices',
        info: 'Invoices you issue to your customers.',
      },
      {
        slug: 'credit-notes',
        url: '/credit-notes',
        name: 'Credit Notes',
        i18n: 'nav.creditNotes',
        info: 'Reduce the amount of an invoice you issued, e.g. for returns or discounts.',
      },
      {
        slug: 'debit-notes',
        url: '/debit-notes',
        name: 'Debit Notes',
        i18n: 'nav.debitNotes',
        info: 'Add extra charges to an invoice you issued.',
      },
      {
        slug: 'refund-notes',
        url: '/refund-notes',
        name: 'Refund Notes',
        i18n: 'nav.refundNotes',
        info: 'Record money you returned to a customer.',
      },
      {
        slug: 'consolidated',
        url: '/consolidated',
        name: 'Consolidated Invoices',
        i18n: 'nav.consolidatedInvoices',
        info: 'Combine many small walk-in (B2C) sales into one e-invoice for LHDN.',
      },
      {
        slug: 'payment-transactions',
        url: '/payment-transactions',
        name: 'Payments Received',
        i18n: 'nav.paymentsReceived',
        info: 'Online payments your customers made against your invoices.',
      },
    ],
  },
  {
    slug: 'purchases',
    url: '/expense-docs/type/invoice',
    name: 'Purchases',
    icon: 'ShoppingCartIcon',
    i18n: 'nav.purchases',
    submenu: [
      {
        slug: 'expense-invoices',
        url: '/expense-docs/type/invoice',
        name: 'Supplier Invoices',
        i18n: 'nav.supplierInvoices',
        info: 'Invoices your suppliers sent you.',
      },
      {
        slug: 'expense-credit-notes',
        url: '/expense-docs/type/credit-note',
        name: 'Supplier Credit Notes',
        i18n: 'nav.supplierCreditNotes',
        info: 'Credit notes from suppliers that reduce what you owe them.',
      },
      {
        slug: 'expense-debit-notes',
        url: '/expense-docs/type/debit-note',
        name: 'Supplier Debit Notes',
        i18n: 'nav.supplierDebitNotes',
        info: 'Debit notes from suppliers that add to what you owe them.',
      },
      {
        slug: 'expense-refund-notes',
        url: '/expense-docs/type/refund-note',
        name: 'Supplier Refund Notes',
        i18n: 'nav.supplierRefundNotes',
        info: 'Refunds your suppliers made to you.',
      },
      {
        slug: 'self-billed',
        url: '/self-billed/type/invoice',
        name: 'Self-Billed',
        icon: 'ListIcon',
        i18n: 'nav.selfBilled',
        submenu: [
          {
            slug: 'self-billed-invoices',
            url: '/self-billed/type/invoice',
            name: 'Self Billed Invoices',
            i18n: 'nav.selfBilledInvoices',
            info: 'Invoices you issue on a supplier\'s behalf when they can\'t, e.g. overseas sellers or individuals.',
          },
          {
            slug: 'self-billed-credit-notes',
            url: '/self-billed/type/credit-note',
            name: 'Self Billed Credit Notes',
            i18n: 'nav.selfBilledCreditNotes',
            info: 'Reduce the amount of a self-billed invoice.',
          },
          {
            slug: 'self-billed-debit-notes',
            url: '/self-billed/type/debit-note',
            name: 'Self Billed Debit Notes',
            i18n: 'nav.selfBilledDebitNotes',
            info: 'Add extra charges to a self-billed invoice.',
          },
          {
            slug: 'self-billed-refund-notes',
            url: '/self-billed/type/refund-note',
            name: 'Self Billed Refund Notes',
            i18n: 'nav.selfBilledRefundNotes',
            info: 'Record a refund on a self-billed invoice.',
          },
        ],
      },
    ],
  },
  {
    slug: 'e-invoicing',
    url: '/e-invoices',
    name: 'e-Invoicing',
    icon: 'RefreshCwIcon',
    i18n: 'nav.eInvoicing',
    submenu: [
      {
        slug: 'lhdn-e-invoice',
        url: '/e-invoices',
        name: 'LHDN e-Invoices',
        i18n: 'nav.lhdnEInvoices',
        info: 'Send your documents to LHDN and check whether they were accepted.',
      },
      {
        slug: 'my-peppol-e-invoice',
        url: '/e-invoices?view=peppol',
        name: 'Peppol e-Invoices',
        i18n: 'nav.peppolEInvoices',
        info: 'Send your documents over the Peppol network and check they were delivered.',
      },
      {
        slug: 'sg-peppol-e-invoice',
        url: '/e-invoices',
        name: 'e-Invoices',
        i18n: 'nav.eInvoices',
        info: 'Send your documents over InvoiceNow and check they were delivered.',
      },
      {
        slug: 'lhdn-received-ap',
        url: '/ap-documents',
        name: 'Supplier e-Invoices (LHDN)',
        i18n: 'nav.supplierEInvoicesLhdn',
        info: 'Supplier e-invoices issued to you on LHDN. You can reject one within 72 hours.',
      },
      {
        slug: 'my-peppol-received-invoices',
        url: '/received-invoices',
        name: 'Supplier e-Invoices (Peppol)',
        i18n: 'nav.supplierEInvoicesPeppol',
        info: 'Supplier documents delivered to you over the Peppol network.',
      },
      {
        slug: 'received-invoices',
        url: '/received-invoices',
        name: 'Supplier Invoices to Respond',
        i18n: 'nav.supplierInvoicesToRespond',
        info: 'Supplier invoices delivered over InvoiceNow. Accept, reject or query them here.',
      },
      {
        slug: 'received-orders',
        url: '/received-orders',
        name: 'Received Orders',
        i18n: 'nav.receivedOrders',
        info: 'Orders other businesses sent you over InvoiceNow (Peppol). Accept or reject them here.',
      },
      {
        slug: 'lhdn-submissions',
        url: '/lhdn-submissions',
        name: 'LHDN Submission History',
        i18n: 'nav.lhdnSubmissionHistory',
        info: 'A log of every batch sent to LHDN and its reply. Mainly for troubleshooting.',
      },
      {
        slug: 'peppol-submissions',
        url: '/my-peppol-submissions',
        name: 'Peppol Submission History',
        i18n: 'nav.peppolSubmissionHistory',
        info: 'A log of every batch sent over Peppol and its reply. Mainly for troubleshooting.',
      },
      {
        slug: 'sg-peppol-submissions',
        url: '/sg-peppol-submissions',
        name: 'Submission History',
        i18n: 'nav.submissionHistory',
        info: 'A log of every batch sent over InvoiceNow and its reply. Mainly for troubleshooting.',
      },
    ],
  },
  {
    slug: 'reports',
    url: '/Reconciliation-Report',
    name: 'Reports',
    icon: 'BarChart2Icon',
    i18n: 'nav.reports',
    submenu: [
      {
        slug: 'reconciliation-report',
        url: '/Reconciliation-Report',
        name: 'Reconciliation',
        i18n: 'nav.rptReconciliation',
        info: 'Checks that every invoice you issued was accepted by LHDN and lists any gaps.',
      },
      {
        slug: 'edocument-by-status-report',
        url: '/eDocument-by-status-report',
        name: 'e-Invoice Status',
        i18n: 'nav.rptEInvoiceStatus',
        info: 'Your e-invoices grouped by LHDN result: valid, invalid, cancelled or pending.',
      },
      {
        slug: 'proof-of-income-validation-report',
        url: '/proof-of-income-validation-report',
        name: 'Validated Sales',
        i18n: 'nav.rptValidatedSales',
        info: 'Sales e-invoices accepted by LHDN. Use as proof of income for banks or auditors.',
      },
      {
        slug: 'proof-of-income-monthly-report',
        url: '/proof-of-income-monthly-report',
        name: 'Monthly Sales',
        i18n: 'nav.rptMonthlySales',
        info: 'Your LHDN-accepted sales, totalled month by month.',
      },
      {
        slug: 'proof-of-expenses-validation-report',
        url: '/proof-of-expenses-validation-report',
        name: 'Validated Purchases',
        i18n: 'nav.rptValidatedPurchases',
        info: 'Purchase e-invoices accepted by LHDN. Use as proof of expenses.',
      },
      {
        slug: 'ap-documents-report',
        url: '/ap-documents-report',
        name: 'Supplier e-Invoices',
        i18n: 'nav.rptSupplierEInvoices',
        info: 'Filter supplier e-invoices from LHDN by date, status, type or supplier, see totals, and export to Excel or PDF.',
      },
      {
        slug: 'email-logs-report',
        url: '/email-logs-report',
        name: 'Email Delivery',
        i18n: 'nav.rptEmailDelivery',
        info: 'Whether the documents you emailed to customers were delivered.',
      },
      {
        slug: 'document-ledger-report',
        url: '/document-ledger-report',
        name: 'All Documents',
        i18n: 'nav.rptAllDocuments',
        info: 'Every document you sent or received in one list, with its e-invoice result.',
      },
      {
        slug: 'my-peppol-transmission-status-report',
        url: '/my-peppol-transmission-status-report',
        name: 'Peppol Delivery Status',
        i18n: 'nav.rptPeppolDelivery',
        info: 'Whether each document you sent over Peppol reached the receiver.',
      },
      {
        slug: 'my-peppol-monthly-compliance-report',
        url: '/my-peppol-monthly-compliance-report',
        name: 'Peppol Monthly Compliance',
        i18n: 'nav.rptPeppolCompliance',
        info: 'Monthly summary required by Malaysia\'s Peppol authority.',
      },
      {
        slug: 'my-peppol-reconciliation-report',
        url: '/my-peppol-reconciliation-report',
        name: 'Peppol Reconciliation',
        i18n: 'nav.rptPeppolReconciliation',
        info: 'Matches documents sent and received over Peppol against your records.',
      },
      {
        slug: 'peppol-transmission-status-report',
        url: '/peppol-transmission-status-report',
        name: 'Peppol Delivery Status',
        i18n: 'nav.rptPeppolDelivery',
        info: 'Whether each document you sent over InvoiceNow reached the receiver and IRAS.',
      },
      {
        slug: 'peppol-monthly-compliance-report',
        url: '/peppol-monthly-compliance-report',
        name: 'Peppol Monthly Compliance',
        i18n: 'nav.rptPeppolCompliance',
        info: 'Monthly summary of the documents reported to IRAS.',
      },
      {
        slug: 'peppol-reconciliation-report',
        url: '/peppol-reconciliation-report',
        name: 'Peppol Reconciliation',
        i18n: 'nav.rptPeppolReconciliation',
        info: 'Matches documents sent and received over InvoiceNow against your records.',
      },
    ],
  },
  {
    slug: 'settings',
    url: '/setting',
    name: 'Settings',
    icon: 'SettingsIcon',
    i18n: 'nav.settings',
    submenu: [
      {
        slug: 'setting',
        url: '/setting',
        name: 'Company Settings',
        i18n: 'nav.companySettings',
        info: 'Your company details, tax registration and e-invoice settings.',
      },
      {
        slug: 'pdf-templates',
        url: '/pdf-templates',
        name: 'PDF Templates',
        i18n: 'nav.pdfTemplates',
        info: 'Choose how your invoices look when printed or emailed.',
      },
      {
        slug: 'roles',
        url: '/roles',
        name: 'Roles',
        i18n: 'nav.roles',
        info: 'Decide what each type of user is allowed to see and do.',
      },
      {
        slug: 'users',
        url: '/users',
        name: 'Users',
        i18n: 'nav.users',
        info: 'Invite people and manage who can sign in.',
      },
      {
        slug: 'audit-trail',
        url: '/audit-trail',
        name: 'Audit Trail',
        i18n: 'nav.auditTrail',
        info: 'See who created, changed or sent what, and when.',
      },
      {
        slug: 'files',
        url: '/files',
        name: 'Imports',
        i18n: 'nav.imports',
        info: 'Spreadsheets you uploaded to create documents in bulk, and whether each one worked.',
      },
    ],
  },
  {
    slug: 'help',
    url: '/faq',
    name: 'Help',
    icon: 'HelpCircleIcon',
    i18n: 'nav.help',
    submenu: [
      {
        slug: 'faq',
        url: '/faq',
        name: 'FAQ',
        i18n: 'nav.faq',
        info: 'Answers to common questions about SmartInvoice.',
      },
      {
        slug: 'csv-import-guide',
        url: '/csv-import-guide',
        name: 'CSV Import Guide',
        i18n: 'nav.csvImportGuide',
        info: 'How to fill in the spreadsheet for bulk uploads.',
      },
      {
        slug: 'about-us',
        url: '/about-us',
        name: 'About Us',
        i18n: 'nav.aboutUs',
        info: 'About the SmartInvoice provider.',
      },
      {
        slug: 'privacy-policy',
        url: '/privacy-policy',
        name: 'Privacy Policy',
        i18n: 'nav.privacyPolicy',
        info: 'How your data is handled.',
      },
      {
        slug: 'terms-and-conditions',
        url: '/terms-and-conditions',
        name: 'Terms and Conditions',
        i18n: 'nav.termsAndConditions',
        info: 'The terms for using SmartInvoice.',
      },
    ],
  },
  {
    slug: 'smart-pos',
    url: '/smart-pos',
    name: 'SmartPos',
    icon: 'ExternalLinkIcon',
    i18n: 'nav.smartPos',
    info: 'Open SmartPOS, your point-of-sale app.',
  },
]
